import React from "react";
import addDays from "date-fns/addDays";
import axios, { AxiosResponse } from "axios";
import moment from 'moment';
import { AlertColor, Button, Link, TextField, useMediaQuery} from "@mui/material";
import { Add, ContentCopy, NoteAdd, Replay } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { BaseControl, CheckboxControl, DatePickerControl, DropDownComboControl, DropDownComboOption, GenericFilter, InputBox } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { PurchaseOrderFilter } from "../../../models/classes/PurchaseOrderFilter";
import { CustomTablePagination } from "../../component/CustomPagination";
import { PurchaseOrder } from "./PurchaseOrderAccessories";
import { GetPurchaseOrderRegion, PurchaseOrderSingle, PurchaseOrderSingleHandle } from "./PurchaseOrderSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { BranchInfo } from "../branches/BranchAccessories";
import { Pad } from "../../../common/Formatter";
import { ManufacturerCombo } from "../manufacturers/ManufacturerAccessories";
import { getDefaultRegion, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { InvoiceSingle, InvoiceSingleHandle } from "../invoices/InvoiceSingle";
import { useNavigate } from "react-router-dom";
import { Invoice } from "../invoices/InvoiceAccessories";
import { defaultContactPerson, defaultDeliveryMethod, defaultInvoiceRegions, defaultPaymentTerm, deliveryAddress, deliveryAddressSS, deliveryAttn, deliveryAttnSS, deliveryPhone, deliveryPhoneSS, deliveryTo, deliveryToSS } from "../../../common/Constants";
import { OpenMode } from "../../../common/Enums";
import { EndUserCombo } from "../end-users/EndUserAccessories";

export function PurchaseOrderList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<PurchaseOrderSingleHandle>(null);

	const singleInvoiceModalRef = React.useRef<InvoiceSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

	const [manufacturers, setManufacturers] = React.useState<DropDownComboOption[]>([]);
	const [selectedPoNos, setSelectedPoNos] = React.useState<string[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
		customerPoNo: "",
        customer: 0,
		manufacturer: 0,
        endUser: 0,
        createdFrom: addDays(new Date(), -60),
        createdTo: new Date(),
		sales: 0,
        region: "ALL",
		isApGen: false,
		isPaid: false,
		isSample: false,
		isOutstanding: false,
		isSpecialDiscount: false,
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData(isSample: boolean) {
		setLoading(true);
		
        let filter = new PurchaseOrderFilter({
			PoNo: searchData.searchText,
			CustomerPoNo: searchData.customerPoNo,
			CustomerID: searchData.customer % 100000,
			ManufacturerID: searchData.manufacturer,
			EndUserID: searchData.endUser,
			From: moment(searchData.createdFrom).format(),
			To: moment(searchData.createdTo).format(),
			StaffID: Number(searchData.sales),
			Region: searchData.region,
			IsAPGen: searchData.isApGen,
			IsPaid: searchData.isPaid,
			IsSample: searchData.isSample || isSample,
			IsOutstanding: searchData.isOutstanding,
			IsSpecialDiscount: searchData.isSpecialDiscount,
			Reader: ""
		});

		axios.post<PurchaseOrder[]>('Api/V1/PurchaseOrder/Search', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.poNo;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.poNo > b.poNo) { return -1; }
						if (a.poNo < b.poNo) { return 1; }
					}
					return 0;					
				}))
				setAllData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.poNo > b.poNo) { return -1; }
						if (a.poNo < b.poNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setAllData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();
		setSelectedPoNos([]);

		let loadedManufacturers: DropDownComboOption[] = [];
		axios.get<ManufacturerCombo[]>('Api/V1/Manufacturer/Combo', {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.map((e) => loadedManufacturers.push({ name: e.name, value: e.manufacturerID.toString() }));
				setManufacturers(loadedManufacturers);
			},
				(error) => {
					console.log(error);
					setLoading(false);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				setLoading(false);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});

		let endUsersDropDownOption: DropDownComboOption[] = [];
		props.endUsers.map(e => endUsersDropDownOption.push({ name: e.endUserName, value: e.endUserId.toString() }));
		setControls([
			{
				key: 'createdFrom',
				title: 'Created From',
				dateValue: addDays(new Date(), -7)
			} as DatePickerControl,
			{
				key: 'createdTo',
				title: 'Created To',
				dateValue: new Date()
			} as DatePickerControl,
			{
				key: 'manufacturer',
				title: 'Manufacturer',
				dropdownOptions: loadedManufacturers
			} as DropDownComboControl,
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl,
			{
				key: 'endUser',
				title: 'End User',
				dropdownOptions: endUsersDropDownOption
			} as DropDownComboControl,
			{
				key: 'region',
				title: 'Region',
				dropdownOptions: props.regions
			} as DropDownComboControl,
			{
				key: 'sales',
				title: 'Sales',
				dropdownOptions: props.sales
			} as DropDownComboControl,
			{
				key: 'customerPoNo',
				title: 'Customer PO',
				value: props.customerPoNo
			} as InputBox,
			{
				key: 'isSample',
				title: 'Is Sample',
				checked: props.isSample
			} as CheckboxControl,
			{
				key: 'isOutstanding',
				title: 'Is Outstanding',
				checked: props.isOutstanding
			} as CheckboxControl
		]);
		LoadData(false);
    }

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'poNo', headerName: 'PO No', flex: 2.2, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString());
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'customerName', headerName: 'Customer', flex: 6, type: 'string' },
		{ field: 'endUser', headerName: 'End User', flex: 6, type: 'string' },
		{ field: 'customerPO', headerName: 'Customer PO', flex: 4, type: 'string', editable: true },
		{ field: 'currency', headerName: 'CCY', flex: 1, type: 'string' },
		{ field: 'createDate', headerName: 'Create Date', flex: 2, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{ field: 'staff', headerName: 'Sales', flex: 2, type: 'string' },
		{ field: 'createdBy', headerName: 'Created By', flex: 2, type: 'string' },
		{ field: 'displayStatus', headerName: 'Status', flex: 1, type: 'string' }
	];

	const [gridData, setGridData] = React.useState<PurchaseOrder[]>([]);
    const [allData, setAllData] = React.useState<PurchaseOrder[]>([]);
	const selectedData = React.useRef<PurchaseOrder[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject(false)}>Create</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<ContentCopy />} onClick={() => CopyObject()}>Copy</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject(true)}>Sample PO</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px", minWidth: "125px" }} startIcon={<NoteAdd />} onClick={() => CreateInvoice(false)}>Create Invoice</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px", minWidth: "125px" }} startIcon={<NoteAdd />} onClick={() => CreateInvoice(true)}>Proforma Invoice</Button>
				<div style={{ flexGrow: 1 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Replay />} onClick={() => Initialise()}>Reload</Button>
			</GridToolbarContainer>
		);
	}

	function NewObject(isSample: boolean) {
		axios.get<BranchInfo[]>(`Api/V1/Branch`, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let defaultBranch: BranchInfo | undefined = result.data.find(b => b.region === getDefaultRegion());
				if (defaultBranch)
				{
					let isShangHai: boolean = defaultBranch.region === "SS";
					let po: PurchaseOrder = {
						id: "",
						poNo: isSample ? "PO" + defaultBranch.japanYear + defaultBranch.region + "SM" + Pad(defaultBranch.samplePONo, 3) : "PO" + defaultBranch.japanYear + defaultBranch.region + Pad(defaultBranch.poNo, 5),
						custLangID: 1,
						customerID: 0,
						customerName: "",
						languageID: 1,
						endUserID: 0,
						endUser: "",
						createDate: moment(new Date()).format(),
						staffID: 0,
						staff: "",
						createdBy: "",
						attention: defaultContactPerson,
						deliveryMethod: defaultDeliveryMethod,
						paymentTerm: defaultPaymentTerm,
						currency: "JPY",
						exRate: 1,
						remark: "注意: 如果貴公司須要由日本--香港--中國, 香港到中國的運貨, 將由貴公司負責, 但不須要做報關, 敬希垂注!",
						purchaseOrderItems: [],
						status: "ACT",
						deliveryTo: isShangHai ? deliveryToSS : deliveryTo,
						deliveryAttention: isShangHai ? deliveryAttnSS : deliveryAttn,
						deliveryAddress: isShangHai ? deliveryAddressSS : deliveryAddress,
						deliveryPhone: isShangHai ? deliveryPhoneSS : deliveryPhone,
						customerPO: "",
						arrivalDate: "",
						purchaseCurrency: "",
						tradingCompany: "",
						tradingCompanyID: 0,
						manufacturerName: manufacturers?.find(x => x.value=="1")?.name ?? "",
						manufacturerID: 1,
						printIndex: 0,
						isSample: isSample,
						displayStatus: "",
						manufacturerAddress: ""
					}
					singleModalRef.current?.Initialise(OpenMode.NEW, po);
				}
			},
				(error) => {
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function CopyObject() {
		if (selectedData.current.length == 0)
		{
			showSnackBar("Please select at least one row to copy.", "", "warning");
			return;
		}
		if (selectedData.current.length > 1)
		{
			var customerId = selectedData.current[0].customerID;
			let poOfDifferentCustomer = selectedData.current.find(x => x.customerID != customerId);
			if (poOfDifferentCustomer)
			{
				showSnackBar("Please select POs of the same customer to create PO.", "", "warning");
				return;
			}
		}

		let poNo = selectedData.current[0].poNo;
		let poNos = Array.prototype.map.call(selectedData.current, p => p.poNo).toString();

		setLoading(true);
        const params = { poNos:  poNos};

		axios.all([
			axios.get(`Api/V1/PurchaseOrder/CopyPo`, {
				params,
				headers: {
				  'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const poResult: AxiosResponse<PurchaseOrder> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let branch: BranchInfo | undefined = branchInfoResult.data.find(b => b.region === GetPurchaseOrderRegion(poNo));
			if (branch)
			{
				poResult.data.poNo = poResult.data.isSample ? "PO" + branch.japanYear + branch.region + "SM" + Pad(branch.samplePONo, 3) : "PO" + branch.japanYear + branch.region + Pad(branch.poNo, 5);
				poResult.data.createDate = moment(new Date()).format();
				poResult.data.createdBy=''; //createBy cannot be null when save
				poResult.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.COPY, poResult.data);
			}			
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		});
	}

	function EditObject(purchaseOrderNo: string) {
        setLoading(true);
		axios.get<PurchaseOrder>('Api/V1/PurchaseOrder/' + purchaseOrderNo, {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				result.data.createdBy=''; //createBy cannot be null when save
				result.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function CreateInvoice(isProforma: boolean) {
		if (selectedData.current.length == 0)
		{
			showSnackBar("Please select at least one row to create invoice.", "", "warning");
			return;
		}
		if (selectedData.current.length > 1)
		{
			var customerId = selectedData.current[0].customerID;
			let poOfDifferentCustomer = selectedData.current.find(x => x.customerID != customerId);
			if (poOfDifferentCustomer)
			{
				showSnackBar("Please select POs of the same customer to create invoice.", "", "warning");
				return;
			}
		}

		let poNo = selectedData.current[0].poNo;
		//let poNos = Array.prototype.map.call(selectedData.current, p => p.poNo).toString();
		let poNos = selectedPoNos.join(',');

		setLoading(true);
        const params = { poNos:  poNos};

		axios.all([
			axios.get(`Api/V1/Invoice/CreateByPo`, {
				params,
				headers: {
				  'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const invoiceResult: AxiosResponse<Invoice> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let branch: BranchInfo | undefined = branchInfoResult.data.find(b => b.region === GetInvoiceRegion(poNo));
			if (branch)
			{
				let invoiceNumber = branch.invoiceNo;
				if (branch.invoiceMonth != new Date().getMonth() + 1)
				{
					invoiceNumber = branch.nextMonthInvoiceNo;
				}
				invoiceResult.data.invoiceNo = isProforma ? "PI" + branch.region : "ME" + branch.region + (new Date().getFullYear()) + "/" + Pad(new Date().getMonth() + 1, 2) + Pad(invoiceNumber, 3);
				invoiceResult.data.isProforma = isProforma;
				invoiceResult.data.createDate = moment(new Date()).format();
				invoiceResult.data.createdBy=''; //createBy cannot be null when save
				invoiceResult.data.status='ACT'; //status cannot be null when save
				singleInvoiceModalRef.current?.Initialise(OpenMode.COPY, invoiceResult.data, false);
			}				
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		});
	}

    function GetInvoiceRegion(poNo: string) {
		let region = poNo.substring(4, 6);
		if (defaultInvoiceRegions.findIndex(x => x == region) > -1)
		  return region;
		else
		  return "HK";  
	}
  
    function DoSearch(event: React.FormEvent<any>) {
		if (event)
        	event.preventDefault();
    
		LoadData(false);
    }

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Purchase Order List" />
			<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="PO No"/>
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					checkboxSelection
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					onSelectionModelChange={(selectionModel) => {
						const ids = new Set(selectionModel);
						const selectedRows = gridData.filter((row) => ids.has(row.poNo));
						selectedData.current = selectedRows;
						if (selectedData.current.length > selectedPoNos.length)
						{
							let newlySelectedRow = selectedData.current.filter((row) => selectedPoNos.indexOf(row.poNo) < 0);
							selectedPoNos.push(newlySelectedRow[0].poNo);
						}
						if (selectedData.current.length < selectedPoNos.length)
						{
							let deletedPoNoIndex = selectedPoNos.findIndex((x) => selectedData.current.findIndex((y) => y.poNo == x) < 0);
							selectedPoNos.splice(deletedPoNoIndex, 1);
							setSelectedPoNos(selectedPoNos);
						}
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
				/>
			</div>

			<PurchaseOrderSingle
				ref={singleModalRef}
				manufacturers={manufacturers}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, po: PurchaseOrder) => {
					LoadData(po.isSample);
				} } 
			/>

			<InvoiceSingle
				ref={singleInvoiceModalRef}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, invoice: Invoice) => {
					if (saved) {
						navigate("/app/invoice");
					}
				} }
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

